import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    StyleSheet, Text, View, TextInput, TouchableOpacity, ScrollView,
} from 'react-native';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { AntDesign, Ionicons } from '@expo/vector-icons';
import { useIsFocused } from '@react-navigation/native';
import Modal from "react-native-modal";
import DataTable from "react-data-table-component";
import Checkbox from 'expo-checkbox';
import { sensingColumns, WorkState, sensingAlramColumns, useScale, ScaleFn , useDarkMode, TablePagination, rssiInfo, lastSeenInfo} from './common';
import Data from './Data';
import Config from '../config.json';
import { getStorage, setStorage } from '../storage';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg:        '#F9FAFB',
    card:      '#FFFFFF',
    cardInner: '#F3F4F6',
    border:    '#E5E7EB',
    text:      '#111827',
    textSub:   '#4B5563',
    muted:     '#9CA3AF',
    trackBg:   'rgba(0,0,0,0.03)',
};
type Theme = typeof DARK;

const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    warn:    '#FB923C',
    replace: '#FBBF24',
    purple:  '#A78BFA',
    pink:    '#F472B6',
};

// ─── 모달 백드롭 드래그 완전 차단 (callback ref) ────────────
// 스크롤 가능한 영역 안에서 발생한 이벤트인지 판별 (모달 내부 드롭다운 등)
const isInScrollable = (target: any) => {
    let n: any = target;
    while (n && n !== document.body && n.nodeType === 1) {
        const ov = getComputedStyle(n).overflowY;
        if ((ov === 'auto' || ov === 'scroll') && n.scrollHeight > n.clientHeight + 1) return true;
        n = n.parentElement;
    }
    return false;
};

const blockDrag = (el: any) => {
    if (!el || el._bd) return;
    el._bd = true;
    const pd = (e: Event) => { if (e.type === 'wheel' && isInScrollable(e.target)) return; e.preventDefault(); };
    el.addEventListener('mousedown', pd);
    el.addEventListener('touchstart', pd, { passive: false });
    el.addEventListener('wheel', pd, { passive: false });
    el.addEventListener('dragstart', pd);
};
// ─── 모달 컨텐츠 드래그 차단 (클릭은 허용, 스크롤만 차단) ────────────
const blockContentDrag = (el: any) => {
    if (!el || el._bcd) return;
    el._bcd = true;
    const pd = (e: Event) => { if (isInScrollable(e.target)) return; e.preventDefault(); e.stopPropagation(); };
    el.addEventListener('wheel', pd, { passive: false });
    el.addEventListener('touchmove', pd, { passive: false });
};

// ─── 상태 닷 ──────────────────────────────────────────
const Dot = ({ color, size }: { color: string; size?: number }) => {
    const { s } = useScale();
    const sz = size ?? s(8);
    return (
    <View style={{
        width: sz, height: sz, borderRadius: sz / 2,
        backgroundColor: color, shadowColor: color,
        shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.6,
        shadowRadius: sz * 0.4, elevation: 2,
    }} />
    );
};

// ─── 섹션 헤더 ────────────────────────────────────────
const SectionTitle = ({ title, dotColor, T, right }: {
    title: string; dotColor?: string; T: Theme; right?: React.ReactNode;
}) => {
    const { scale, s } = useScale();
    const st = useMemo(() => makeSt(s), [scale]);
    return (
    <View style={st.sectionHeader}>
        <Dot color={dotColor ?? S.accent} />
        <Text style={[st.sectionTitle, { color: T.text }]}>{title}</Text>
        {right}
    </View>
    );
};

// ─── 정보 행 ──────────────────────────────────────────
const InfoRow = ({ label, value, T, editable, onChangeText, copyable }: {
    label: string; value: string; T: Theme; editable?: boolean; onChangeText?: (t: string) => void; copyable?: boolean;
}) => {
    const { scale, s } = useScale();
    const st = useMemo(() => makeSt(s), [scale]);
    const [copied, setCopied] = useState(false);
    const onCopy = () => {
        const text = String(value || '');
        if (!text) return;
        try {
            if (navigator?.clipboard?.writeText) {
                navigator.clipboard.writeText(text);
            } else {
                const ta = document.createElement('textarea');
                ta.value = text; document.body.appendChild(ta); ta.select();
                document.execCommand('copy'); document.body.removeChild(ta);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch (e) { /* noop */ }
    };
    return (
    <View style={[st.infoRow, { borderBottomColor: T.border }]}>
        <Text style={[st.infoLabel, { color: T.textSub }]}>{label}</Text>
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
            {editable && onChangeText ? (
                <TextInput
                    autoCapitalize="none"
                    style={[st.infoInput, { flex: 1, color: T.text, borderColor: S.accent + '50', backgroundColor: T.cardInner }]}
                    value={value}
                    onChangeText={onChangeText}
                />
            ) : (
                <Text style={[st.infoValue, { flex: 1, color: T.text }]}>{value || '-'}</Text>
            )}
            {copyable && (
                <TouchableOpacity onPress={onCopy} style={{ marginLeft: s(6), padding: s(1) }} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                    <Ionicons name={copied ? 'checkmark' : 'copy-outline'} size={s(13)} color={copied ? S.ok : S.accent} />
                </TouchableOpacity>
            )}
        </View>
    </View>
    );
};

// ─── 상태 뱃지 ────────────────────────────────────────
const StatusBadge = ({ text, color, active, onPress, T }: { text: string; color: string; active?: boolean; onPress?: () => void; T?: any }) => {
    const { scale, s } = useScale();
    const st = useMemo(() => makeSt(s), [scale]);
    const on = active !== false;
    return (
    <TouchableOpacity onPress={onPress} activeOpacity={0.7}
        style={[st.badge, {
            backgroundColor: on ? color : (T?.cardInner ?? 'transparent'),
            borderWidth: 1.5, borderColor: on ? color : (T?.border ?? color + '40'),
        }]}>
        <Text style={{ fontSize: s(14), fontWeight: '700', color: on ? '#fff' : (T?.textSub ?? color) }}>{text}</Text>
    </TouchableOpacity>
    );
};

// ─── 토글 칩 ──────────────────────────────────────────
const ToggleChip = ({ label, active, color, onPress, T }: {
    label: string; active: boolean; color: string; onPress?: () => void; T: Theme;
}) => {
    const { scale, s } = useScale();
    const st = useMemo(() => makeSt(s), [scale]);
    return (
    <TouchableOpacity
        onPress={onPress}
        style={[st.toggleChip, {
            backgroundColor: active ? color + '18' : T.cardInner,
            borderColor: active ? color : T.border,
        }]}
    >
        <View style={{
            width: s(8), height: s(8), borderRadius: s(4),
            backgroundColor: active ? color : T.muted as string,
        }} />
        <Text style={{ fontSize: s(12), fontWeight: '700', color: active ? color : T.muted as string }}>
            {label}
        </Text>
    </TouchableOpacity>
    );
};

// ─── 휠 타임피커 (갤럭시 알람 스타일) ─────────────────────
const WHEEL_H = 48;
const WheelPicker = React.forwardRef(({ value, max, onChange, color, T, onEditDone }: {
    value: number; max: number; onChange: (v: number) => void; color: string; T: Theme;
    onEditDone?: () => void;
}, fRef: any) => {
    const { s } = useScale();
    const WHEEL_H = s(48);
    const scrollRef = useRef<ScrollView>(null);
    const containerRef = useRef<any>(null);
    const timer = useRef<any>(null);
    const ready = useRef(false);
    const skipScroll = useRef(false);
    const valRef = useRef(value);
    valRef.current = value;
    const [editing, setEditing] = useState(false);
    const [editText, setEditText] = useState('');
    const items = Array.from({ length: max + 1 }, (_, i) => i);

    useEffect(() => {
        const t = setTimeout(() => {
            scrollRef.current?.scrollTo({ y: value * WHEEL_H, animated: false });
            ready.current = true;
        }, 60);
        return () => clearTimeout(t);
    }, []);

    // 컨테이너에서 이벤트 전파 차단 → 부모 blockDrag/blockContentDrag 영향 안 받음
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const sp = (e: Event) => e.stopPropagation();
        el.addEventListener('mousedown', sp);
        el.addEventListener('touchstart', sp);
        el.addEventListener('wheel', sp);
        el.addEventListener('touchmove', sp);
        return () => {
            el.removeEventListener('mousedown', sp);
            el.removeEventListener('touchstart', sp);
            el.removeEventListener('wheel', sp);
            el.removeEventListener('touchmove', sp);
        };
    }, []);

    // 부모에서 startEdit 호출 가능
    React.useImperativeHandle(fRef, () => ({
        startEdit: () => {
            setEditText('');
            setEditing(true);
        },
    }));

    const onScroll = (e: any) => {
        if (!ready.current || skipScroll.current) return;
        if (timer.current) clearTimeout(timer.current);
        const y = e.nativeEvent.contentOffset.y;
        timer.current = setTimeout(() => {
            const idx = Math.round(y / WHEEL_H);
            const v = Math.max(0, Math.min(idx, max));
            onChange(v);
            scrollRef.current?.scrollTo({ y: v * WHEEL_H, animated: true });
        }, 120);
    };

    const startEdit = () => { setEditText(''); setEditing(true); };

    const commitEdit = (text?: string) => {
        const t = text ?? editText;
        const n = parseInt(t, 10);
        if (!isNaN(n) && n >= 0 && n <= max) {
            skipScroll.current = true;
            onChange(n);
            setTimeout(() => {
                scrollRef.current?.scrollTo({ y: n * WHEEL_H, animated: true });
                setTimeout(() => { skipScroll.current = false; }, 300);
            }, 50);
        }
        setEditing(false);
        if (onEditDone) onEditDone();
    };

    const p2 = (n: number) => String(n).padStart(2, '0');
    return (
        <View ref={containerRef} style={{
            height: WHEEL_H * 3, width: s(62), overflow: 'hidden',
            borderRadius: s(12), backgroundColor: T.cardInner,
        }}>
            <View pointerEvents="none" style={{
                position: 'absolute', top: WHEEL_H, left: s(3), right: s(3),
                height: WHEEL_H, backgroundColor: color + '18',
                borderRadius: s(8), borderWidth: 1.5, borderColor: color + '40',
                zIndex: 2,
            }} />
            {editing ? (
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', zIndex: 5 }}>
                    <TextInput
                        autoFocus
                        keyboardType="number-pad"
                        maxLength={2}
                        value={editText}
                        onChangeText={(txt) => {
                            const filtered = txt.replace(/[^0-9]/g, '');
                            if (!filtered) { setEditText(''); return; }
                            const n = parseInt(filtered, 10);
                            // 첫 자리만 입력했는데 이미 max의 십의자리보다 크면 바로 커밋
                            if (filtered.length === 1 && n > Math.floor(max / 10)) {
                                setTimeout(() => commitEdit(String(Math.min(n, max))), 50);
                                return;
                            }
                            // 2자리 입력 시 max 클램핑 후 커밋
                            if (filtered.length >= 2) {
                                const clamped = String(Math.min(n, max)).padStart(2, '0');
                                setTimeout(() => commitEdit(clamped), 50);
                                return;
                            }
                            setEditText(filtered);
                        }}
                        onBlur={() => {
                            if (editText.length > 0) {
                                const n = Math.min(parseInt(editText, 10) || 0, max);
                                commitEdit(String(n));
                            } else setEditing(false);
                        }}
                        onSubmitEditing={() => commitEdit()}
                        style={{
                            width: s(52), height: WHEEL_H, borderRadius: s(8),
                            backgroundColor: color + '18', borderWidth: 1.5, borderColor: color,
                            textAlign: 'center', fontSize: s(22), fontWeight: '800', color: color,
                            outlineColor: color,
                        } as any}
                        selectTextOnFocus
                    />
                </View>
            ) : (
                <ScrollView
                    ref={scrollRef}
                    showsVerticalScrollIndicator={false}
                    snapToInterval={WHEEL_H}
                    decelerationRate="fast"
                    onScroll={onScroll}
                    scrollEventThrottle={16}
                    contentContainerStyle={{ paddingVertical: WHEEL_H }}
                    nestedScrollEnabled
                >
                    {items.map(i => (
                        <TouchableOpacity key={i} activeOpacity={0.7}
                            onPress={() => {
                                if (i === value) { startEdit(); return; }
                                onChange(i);
                                scrollRef.current?.scrollTo({ y: i * WHEEL_H, animated: true });
                            }}
                            style={{ height: WHEEL_H, justifyContent: 'center', alignItems: 'center' }}>
                            <Text style={{
                                fontSize: s(22), fontWeight: '800',
                                color: i === value ? color : T.text,
                                opacity: i === value ? 1 : 0.25,
                            }}>{p2(i)}</Text>
                        </TouchableOpacity>
                    ))}
                </ScrollView>
            )}
        </View>
    );
});

// ─── 특이사항 맵 ──────────────────────────────────────
const STATUS_MAP: Record<number, { label: string; color: string }> = {
    1: { label: '욕창',      color: S.err     },
    2: { label: '욕창위험',  color: S.warn    },
    3: { label: '낙상',      color: S.replace },
    4: { label: '요로감염',  color: S.purple  },
};

// ────────────────────────────────────────────────────────
// 메인
// ────────────────────────────────────────────────────────
export default function SensorDetail({ navigation, route }: any) {
    const { scale, s } = useScale();
    const st = useMemo(() => makeSt(s), [scale]);
    const [__p] = useState(() => {
        if (typeof window === 'undefined') return route?.params || {};
        try {
            const id = route?.params?.id;
            const key = `_navParams_SensorDetail_${id ?? ''}`;
            const saved = window.localStorage.getItem(key);
            if (saved) return JSON.parse(saved);
        } catch {}
        return route?.params || {};
    });
    const data = __p?.data;
    const isFocused = useIsFocused();
    const { dark, setDark } = useDarkMode();

    useEffect(() => {
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
        getStorage('AutoLogin').then((v: any) => { if (v?.admin !== undefined) setAdmin(v.admin); });
    }, [isFocused]);

    const T = dark ? DARK : LIGHT;

    useEffect(() => {
        navigation.setOptions({
            headerStyle: { backgroundColor: '#304156', elevation: 0, shadowOpacity: 0, borderBottomWidth: 0 },
            headerTintColor: '#fff',
            headerTitleStyle: { color: '#fff', fontSize: s(20) },
        });
    }, [dark]);

    // ─── 모달 열릴 때 바깥 스크롤/드래그 완전 차단 ─────
    useEffect(() => {
        const anyModal = sensitivityModal || detectionModal || roomModal || calendarModal || modalVisible || deleteModal || sensorDeleteModal || overrideModal;
        if (!anyModal) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const prevent = (e: Event) => { if (isInScrollable(e.target)) return; e.preventDefault(); e.stopPropagation(); };
        document.addEventListener('wheel', prevent, { passive: false } as any);
        document.addEventListener('touchmove', prevent, { passive: false } as any);
        return () => {
            document.body.style.overflow = prev;
            document.removeEventListener('wheel', prevent);
            document.removeEventListener('touchmove', prevent);
        };
    }, [sensitivityModal, detectionModal, roomModal, calendarModal, modalVisible, deleteModal, sensorDeleteModal]);

    // ─── 마운트 가드 (모달 깨짐 방지) ────────────────
    const readyRef = useRef(false);
    useEffect(() => {
        const t = setTimeout(() => { readyRef.current = true; }, 400);
        return () => clearTimeout(t);
    }, []);

    // ─── 기존 state ──────────────────────────────────
    const [focus, setFocus]               = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [deleteModal, setDeleteModal]   = useState(false);
    const [admin, setAdmin]               = useState('1');
    const [sensorDeleteModal, setSensorDeleteModal] = useState(false);
    // ─── 특수기능(관리자 전용) 오버라이드 모달 ─────────────
    const [overrideModal, setOverrideModal] = useState(false);
    const [ovrMsg, setOvrMsg] = useState('');
    const [ovrOpen, setOvrOpen]     = useState({ CAP1: '', CAP2: '' });
    const [ovrBase, setOvrBase]     = useState({ CAP1: '', CAP2: '' });
    const [ovrRep, setOvrRep]       = useState({ REPEATER_ID: '', C_REPEATER: '', U_REPEATER: '' });
    const sendOverride = async (path: string, body: any, label: string) => {
        try {
            setOvrMsg(`${label} 전송 중...`);
            const res = await fetch(`${(Config as any)?.APIserver || '/'}gwsensor/${path}`, {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const j = await res.json();
            if (j?.success) setOvrMsg(`✅ ${label} 적용 완료`);
            else setOvrMsg(`❌ ${label} 실패: ${j?.error || 'unknown'}`);
        } catch (e: any) {
            setOvrMsg(`❌ ${label} 오류: ${e?.message || e}`);
        }
    };
    const num = (v: string) => (v.trim() === '' ? null : Number(v));
    const [ovrCurrent, setOvrCurrent] = useState<any>(null);
    const loadOverrideValues = async () => {
        const id = orignID || sensorID;
        if (!id) return;
        try {
            setOvrMsg('현재값 불러오는 중...');
            const res = await fetch(`${(Config as any)?.APIserver || '/'}gwsensor/getSensorOverrideValues`, {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify({ SENSOR_ID: id }),
            });
            const j = await res.json();
            if (j?.success) {
                setOvrCurrent(j);
                setOvrOpen({ CAP1: j.open?.CAP1 != null ? String(j.open.CAP1) : '', CAP2: j.open?.CAP2 != null ? String(j.open.CAP2) : '' });
                setOvrBase({ CAP1: j.base?.CAP1 != null ? String(j.base.CAP1) : '', CAP2: j.base?.CAP2 != null ? String(j.base.CAP2) : '' });
                setOvrRep({ REPEATER_ID: j.REPEATER_ID || '', C_REPEATER: j.C_REPEATER || '', U_REPEATER: j.U_REPEATER || '' });
                setOvrMsg(j.fromDisk ? '현재값 불러옴 (disk)' : '현재값 불러옴');
            } else {
                setOvrMsg(`❌ 현재값 조회 실패: ${j?.error || ''}`);
            }
        } catch (e: any) {
            setOvrMsg(`❌ 조회 오류: ${e?.message || e}`);
        }
    };
    const [checkedItems, setCheckedItems] = useState<any>([]);
    const [manager, setManager]           = useState([]);
    const [sensorHistory, setSensorHistory]           = useState<any>([]);
    const [SensorAlarmHistory, setSensorAlarmHistory] = useState<any>([]);
    const [histTab, setHistTab] = useState<'sensing' | 'alarm'>('sensing');   // 감지/알림 이력 탭
    const [MemosList, setMemosList] = useState<any>([]);

    const [phone, setPhone]               = useState(data?.PHONE || '');
    const [address, setAddress]           = useState(data?.ADDRESS || '');
    const [guardian, setGuardian]         = useState(data?.GUARDIAN_NAME || '');
    const [guardianPhone, setGuardianPhone] = useState(data?.GUARDIAN_PHONE || '');
    const [repeaterID, setRepeaterID]     = useState(data?.REPEATER_ID || '');
    const [sensorID, setSensorID]         = useState(data?.SENSOR_ID || '');
    const [roomNo, setRoomNo]             = useState(data?.ROOM_NO || '');
    const [bed, setBed]                   = useState(data?.BED_NO || '');
    const [memo, setMemo]                 = useState(data?.MEMO || '');
    const [orignID]                       = useState(data?.ORIGINAL_ID || '');
    const [repeaterList, setRepeaterList] = useState<any[]>([]);
    const [roomOpen, setRoomOpen]         = useState(false);

    // ─── 새 기능 state ───────────────────────────────
    // 움직임 감지 설정
    const [detectionSt, setDetectionSt]     = useState(data?.DETECTION_ST || '');
    const [detectionEnd, setDetectionEnd]   = useState(data?.DETECTION_END || '');
    const [detectionAlarm, setDetectionAlarm] = useState(data?.DETECTION_ALARM ?? 0);
    // 소변감지 알림 (SENSORS.ALARM: 0=꺼짐, 1=켜짐, 기본 켜짐)
    const [urineAlarm, setUrineAlarm] = useState(data?.ALARM ?? 1);
    // 기저귀 설정
    const [diaperShape, setDiaperShape] = useState(data?.CLASSIFICATION ?? 0); // 0=일자형, 1=삼각체결
    const [diaperType, setDiaperType]   = useState(data?.DIAPER_CODE || '');
    const [diaperList, setDiaperList]   = useState<any[]>([]);
    const [diaperOpen, setDiaperOpen]   = useState(false);
    // 기저귀 교체 내역
    const [diaperDate, setDiaperDate]       = useState(moment().format('YYYY-MM-DD'));
    const [diaperHistory, setDiaperHistory] = useState<any[]>([]);
    const [diaperCount, setDiaperCount]     = useState(0);
    const [calendarModal, setCalendarModal] = useState(false);
    const [calMonth, setCalMonth]           = useState(moment());
    // 중계기 변경 내역
    const [repeaterHistory, setRepeaterHistory] = useState<any[]>([]);

    // ── 커스텀 테이블 페이지네이션 상태 ──
    const [hisPage, setHisPage] = useState(1);   const [hisPer, setHisPer] = useState(5);
    const [almPage, setAlmPage] = useState(1);   const [almPer, setAlmPer] = useState(5);
    const [mgrPage, setMgrPage] = useState(1);   const [mgrPer, setMgrPer] = useState(5);
    const [dpPage, setDpPage] = useState(1);     const [dpPer, setDpPer] = useState(10);
    const _hisCur = Math.min(Math.max(1, hisPage), Math.max(1, Math.ceil(((sensorHistory)||[]).length / hisPer)));
    const hisData = ((sensorHistory)||[]).slice((_hisCur-1)*hisPer, _hisCur*hisPer);
    const _almCur = Math.min(Math.max(1, almPage), Math.max(1, Math.ceil(((SensorAlarmHistory)||[]).length / almPer)));
    const almData = ((SensorAlarmHistory)||[]).slice((_almCur-1)*almPer, _almCur*almPer);
    const _mgrCur = Math.min(Math.max(1, mgrPage), Math.max(1, Math.ceil(((manager)||[]).length / mgrPer)));
    const mgrData = ((manager)||[]).slice((_mgrCur-1)*mgrPer, _mgrCur*mgrPer);
    const _dpCur = Math.min(Math.max(1, dpPage), Math.max(1, Math.ceil(((diaperHistory)||[]).length / dpPer)));
    const dpData = ((diaperHistory)||[]).slice((_dpCur-1)*dpPer, _dpCur*dpPer);

    // 방 이동 모달
    const [roomModal, setRoomModal] = useState(false);
    const [selectedRoom, setSelectedRoom] = useState<any>(null);

    const [sensitivity, setSensitivity] = useState(data?.SENSITIVITY ?? 3);
    const [sensitivityModal, setSensitivityModal] = useState(false);
    const [selectedSensitivity, setSelectedSensitivity] = useState(data?.SENSITIVITY ?? 0);
    const SensitivityLabel = sensitivity === 0 ? '강' : sensitivity === 1 ? '중' : sensitivity === 2 ? '약' : '테스트';
    // 움직임 감지 시간 모달
    const [detectionModal, setDetectionModal] = useState(false);
    const [tmpSt, setTmpSt] = useState('');
    const [tmpEnd, setTmpEnd] = useState('');
    const [stH, setStH] = useState(8);
    const [stM, setStM] = useState(0);
    const [endH, setEndH] = useState(18);
    const [endM, setEndM] = useState(0);
    const stHRef = useRef<any>(null);
    const stMRef = useRef<any>(null);
    const endHRef = useRef<any>(null);
    const endMRef = useRef<any>(null);

    // 감지 시간 포맷
    const fmtTime = (v: any) => {
        if (!v) return '--:--';
        const d = new Date(v);
        if (isNaN(d.getTime())) return v;
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    const onFocus = () => {
        if (focus) {
            setPhone(data?.PHONE || '');
            setAddress(data?.ADDRESS || '');
            setGuardian(data?.GUARDIAN_NAME || '');
            setGuardianPhone(data?.GUARDIAN_PHONE || '');
            setRoomNo(data?.ROOM_NO || '');
            setBed(data?.BED_NO || '');
            setRepeaterID(data?.REPEATER_ID || '');
            setSensorID(data?.SENSOR_ID || '');
            setMemo(data?.MEMO || '');
            setRoomOpen(false);
        }
        setFocus(!focus);
    };

    // ─── API ──────────────────────────────────────────
    const getSensoManagerList = () => {
        Data.fetchData('sensor/getSensorInChargeList', { SENSOR_ID: sensorID }, (result: any, count: any) => {
            if (count !== -1) setManager(result);
        }, true);
    };

    const loadSensorHistory = () => {
        Data.fetchData('sensor/getSensorHistory', { ID: sensorID }, (result: any, count: any) => {
            if (count > 0) setSensorHistory(result);
            else setSensorHistory([]);
        }, true);
    };

    const loadSensorAlarm = () => {
        Data.fetchData('sensor/getSensorAlarmHistory', { ID: sensorID }, (result: any, count: any) => {
            if (count > 0) setSensorAlarmHistory(result);
            else setSensorAlarmHistory([]);
        }, true);
    };

    useEffect(() => {
        let mounted = true;
        Data.fetchData('sensor/getSensorInChargeList', { SENSOR_ID: sensorID }, (result: any, count: any) => {
            if (mounted && count !== -1) setManager(result);
        }, true);
        Data.fetchData('sensor/getSensorHistory', { ID: sensorID }, (result: any, count: any) => {
            if (mounted && count > 0) setSensorHistory(result);
        }, true);
        Data.fetchData('sensor/getSensorAlarmHistory', { ID: sensorID }, (result: any, count: any) => {
            if (mounted && count > 0) setSensorAlarmHistory(result);
        }, true);
        Data.fetchData('tabSensor/getMemosList', {
            SENSOR_ID: sensorID, NAME: data?.NAME,
            BIRTH: data?.BIRTH ? moment(data.BIRTH).format('YYYY-MM-DD') : ''
        }, (result: any, count: any) => {
            if (mounted && count > 0) setMemosList(result);
        }, true);
        Data.fetchData('tabSensor/getDiaperList', {}, (result: any, count: any) => {
            if (mounted && count > 0) setDiaperList(result.filter((d: any) => d.USE_YN === 0));
        }, true);
        Data.fetchData('repeater/getRepeatersList', { AREA: data?.AREA || '' }, (result: any, count: any) => {
            if (mounted && count > 0) setRepeaterList(result);
        }, true);
        // 기저귀 교체 내역 (오늘 날짜)
        const today = moment().format('YYYY-MM-DD');
        Data.fetchData('sensor/dateTimeFilteredHistory2', {
            SENSOR_ID: data?.ORIGINAL_ID || sensorID,
            STARTDATE: today, ENDDATE: today,
        }, (result: any, count: any) => {
            if (mounted && count > 0 && Array.isArray(result)) {
                const events = result.filter((r: any) => r.start && r.end);
                setDiaperHistory(events.map((e: any, i: number) => ({ ...e, _no: i + 1 })));
                setDiaperCount(events.length);
            }
        }, true);
        return () => { mounted = false; };
    }, [isFocused]);

    const onSensorUpdate = () => {
        Data.fetchData('sensor/updateSensorInfo', {
            IDX: data?.IDX,
            PHONE: phone,
            ADDRESS: address,
            NAME: data?.NAME || '',
            GENDER: data?.GENDER || '',
            BIRTH: data?.BIRTH ? moment(data.BIRTH).format('YYYY-MM-DD') : null,
            SENSOR_ID: sensorID,
            REPEATER_ID: repeaterID,
            GUARDIAN_NAME: guardian,
            GUARDIAN_PHONE: guardianPhone,
            ROOM_NO: roomNo,
            BED_NO: bed || 0,
            SENSITIVITY: sensitivity,
            CLASSIFICATION: diaperShape,
            DIAPER_CODE: diaperType || 'D0',
        }, (_: any, count: any) => {
            if (count === 1) { alert('저장되었습니다.'); setFocus(false); }
            else alert('저장에 실패하였습니다.');
        }, true);
    };

    // ─── 방 이동 ──────────────────────────────────────
    const openRoomModal = () => {
        if (!readyRef.current) return;
        setSelectedRoom(null);
        setRoomModal(true);
    };
    const onRoomTransfer = () => {
        if (!selectedRoom) { alert('이동할 방을 선택해주세요.'); return; }
        if (selectedRoom.ROOM_NO === roomNo) { alert('현재 방과 동일합니다.'); return; }
        // 대상 중계기의 센서 수 체크
        Data.fetchData('repeater/getSensorListByRepeater',
            { REPEATER_ID: selectedRoom.ID },
            (sResult: any, sCount: any) => {
                const cnt = sCount > 0 ? sResult.length : 0;
                if (cnt >= 6) {
                    alert(`${selectedRoom.ROOM_NO}에 이미 센서가 ${cnt}개 등록되어 있어 이동할 수 없습니다. (최대 6개)`);
                    return;
                }
                Data.fetchData('sensor/updateSensorInfo', {
                    IDX: data?.IDX,
                    PHONE: phone,
                    ADDRESS: address,
                    NAME: data?.NAME || '',
                    GENDER: data?.GENDER || '',
                    BIRTH: data?.BIRTH ? moment(data.BIRTH).format('YYYY-MM-DD') : null,
                    SENSOR_ID: sensorID,
                    REPEATER_ID: selectedRoom.ID,
                    GUARDIAN_NAME: guardian,
                    GUARDIAN_PHONE: guardianPhone,
                    ROOM_NO: selectedRoom.ROOM_NO,
                    BED_NO: bed || 0,
                    SENSITIVITY: sensitivity,
                    CLASSIFICATION: diaperShape,
                    DIAPER_CODE: diaperType || 'D0',
                }, (_: any, count: any) => {
                    if (count === 1) {
                        setRoomNo(selectedRoom.ROOM_NO);
                        setRepeaterID(selectedRoom.ID);
                        setRoomModal(false);
                        alert(`${roomNo} → ${selectedRoom.ROOM_NO}(으)로 이동되었습니다.`);
                    } else alert('방 이동에 실패하였습니다.');
                }, true);
            }, true);
    };

    const onSensorDelete = () => {
        Data.fetchData('sensor/deleteSensor', {
            IDX: data?.IDX, SENSOR_ID: sensorID
        }, (_: any, count: any) => {
            if (count === 1) { alert('삭제되었습니다.'); navigation.goBack(); }
            else alert('삭제에 실패하였습니다.');
        }, true);
    };

    // ─── 센서감도 설정 API ────────────────────────────
    const openSensitivityModal = () => {
        if (!readyRef.current) return;
        setSelectedSensitivity(sensitivity);
        setSensitivityModal(true);
    };
    const onSensitivityConfirm = () => {
        Data.fetchData('tabSensor/updateSensitivity', {
            IDX: data?.IDX, SENSITIVITY: selectedSensitivity,
        }, (_: any, count: any) => {
            if (count >= 1) {
                setSensitivity(selectedSensitivity);
                setSensitivityModal(false);
                alert('센서감도가 변경되었습니다.');
            } else {
                alert('센서감도 변경에 실패하였습니다.');
            }
        }, true);
    };
    // ─── 움직임 감지 시간 설정 모달 ─────────────────
    const openDetectionModal = () => {
        if (!readyRef.current) return;
        const st = fmtTime(detectionSt);
        const en = fmtTime(detectionEnd);
        const [sh, sm] = st.split(':').map(Number);
        const [eh, em] = en.split(':').map(Number);
        setStH(isNaN(sh) ? 8 : sh); setStM(isNaN(sm) ? 0 : sm);
        setEndH(isNaN(eh) ? 18 : eh); setEndM(isNaN(em) ? 0 : em);
        setDetectionModal(true);
    };
    const pad2 = (n: number) => String(n).padStart(2, '0');
    const onDetectionTimeConfirm = () => {
        const stTotal = stH * 60 + stM;
        const enTotal = endH * 60 + endM;
        if (stTotal >= enTotal) {
            alert('시작 시간이 종료 시간보다 같거나 늦을 수 없습니다.');
            return;
        }
        const stStr = `2000-01-01T${pad2(stH)}:${pad2(stM)}:00`;
        const enStr = `2000-01-01T${pad2(endH)}:${pad2(endM)}:00`;
        Data.fetchData('tabSensor/updateSensorStandupDetectionTime', {
            DETECTION_ST: stStr, DETECTION_END: enStr, SENSOR_ID: sensorID,
        }, (_: any, count: any) => {
            if (count >= 1) {
                setDetectionSt(`2000-01-01T${pad2(stH)}:${pad2(stM)}:00`);
                setDetectionEnd(`2000-01-01T${pad2(endH)}:${pad2(endM)}:00`);
                setDetectionModal(false);
                alert('감지 시간이 설정되었습니다.');
            } else alert('설정에 실패하였습니다.');
        }, true);
    };
    // ─── 움직임 알림 켜짐/꺼짐 토글 ─────────────────
    const toggleDetectionAlarm = () => {
        const next = detectionAlarm ? 0 : 1;
        Data.fetchData('tabSensor/standUpDetectionOnOff', {
            DETECTION_ALARM: next, SENSOR_ID: sensorID,
        }, (_: any, count: any) => {
            if (count >= 1) {
                setDetectionAlarm(next);
                alert(next ? '움직임 알림이 켜졌습니다.' : '움직임 알림이 꺼졌습니다.');
            } else alert('변경에 실패하였습니다.');
        }, true);
    };
    // ─── 소변감지 알림 토글 ─────────────────────────
    const toggleUrineAlarm = () => {
        const next = urineAlarm ? 0 : 1;
        Data.fetchData('tabSensor/updateSensorAlarm', {
            ALARM: next, SENSOR_ID: sensorID,
        }, (_: any, count: any) => {
            if (count >= 1) {
                setUrineAlarm(next);
                alert(next ? '소변감지 알림이 켜졌습니다.' : '소변감지 알림이 꺼졌습니다.');
            } else alert('변경에 실패하였습니다.');
        }, true);
    };
    // ─── 특이사항 토글 API ─────────────────────────────
    const refreshMemosList = () => {
        Data.fetchData('tabSensor/getMemosList', {
            SENSOR_ID: sensorID, NAME: data?.NAME,
            BIRTH: data?.BIRTH ? moment(data.BIRTH).format('YYYY-MM-DD') : ''
        }, (result: any, count: any) => {
            if (count > 0) setMemosList(result);
            else setMemosList([]);
        }, true);
    };
    const toggleMemoStatus = (statusCode: number) => {
        const label = STATUS_MAP[statusCode]?.label || '';
        Data.fetchData('tabSensor/toggleMemoStatus', {
            SENSOR_ID: sensorID,
            NAME: data?.NAME || '',
            BIRTH: data?.BIRTH ? moment(data.BIRTH).format('YYYY-MM-DD') : '',
            STATUS: statusCode,
        }, (_: any, count: any) => {
            if (count === 1) {
                refreshMemosList();
                alert(`"${label}" 상태가 해제되었습니다.`);
            } else if (count >= 2) {
                refreshMemosList();
                alert(`"${label}" 상태가 설정되었습니다.`);
            } else {
                alert('상태 변경에 실패하였습니다.');
            }
        }, true);
    };

    // ─── 기저귀 분류 변경 API ───────────────────────────
    const updateClassification = (val: number) => {
        if (val === diaperShape) return;
        setDiaperShape(val);
        Data.fetchData('tabSensor/updateClassification', {
            IDX: data?.IDX, CLASSIFICATION: val,
        }, (_: any, count: any) => {
            if (count >= 1) alert(val === 0 ? '일자형으로 변경되었습니다.' : '삼각체결로 변경되었습니다.');
            else alert('분류 변경에 실패하였습니다.');
        }, true);
    };
    // ─── 기저귀 종류 변경 API ───────────────────────────
    const updateDiaperCode = (code: string) => {
        if (code === diaperType) { setDiaperOpen(false); return; }
        setDiaperType(code);
        setDiaperOpen(false);
        const name = diaperList.find((d: any) => d.DIAPER_CODE === code)?.DIAPER_NAME || code;
        Data.fetchData('tabSensor/updateDiaper', {
            IDX: data?.IDX, DIAPER_CODE: code,
        }, (_: any, count: any) => {
            if (count >= 1) alert(`기저귀 종류가 "${name}"(으)로 변경되었습니다.`);
            else alert('기저귀 종류 변경에 실패하였습니다.');
        }, true);
    };

    const onDiaperChange = () => {
        Data.fetchData('tabSensor/updateSensorReplacement', {
            SENSOR_ID: data?.ORIGINAL_ID || sensorID,
            REPLACEMENT: 1,
        }, (_: any, count: any) => {
            if (count === 1) {
                alert('기저귀 교체가 완료되었습니다.');
                searchDiaperHistory();
            } else alert('기저귀 교체에 실패하였습니다.');
        }, true);
    };
    const searchDiaperHistory = () => {
        Data.fetchData('sensor/dateTimeFilteredHistory2', {
            SENSOR_ID: data?.ORIGINAL_ID || sensorID,
            STARTDATE: diaperDate,
            ENDDATE: diaperDate,
        }, (result: any, count: any) => {
            if (count > 0 && Array.isArray(result)) {
                // 마지막 항목은 통계 객체 → 제외
                const events = result.filter((r: any) => r.start && r.end);
                setDiaperHistory(events.map((e: any, i: number) => ({ ...e, _no: i + 1 })));
                setDiaperCount(events.length);
            } else {
                setDiaperHistory([]);
                setDiaperCount(0);
            }
        }, true);
    };

    // ─── DataTable 스타일 ─────────────────────────────
    const tableStyles = {
        headRow:    { style: { backgroundColor: T.cardInner, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(40) } },
        headCells:  { style: { color: T.textSub, fontSize: s(12), fontWeight: '600' as any, paddingLeft: s(12), paddingRight: s(12) } },
        rows:       { style: { backgroundColor: T.card, color: T.text, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(44), '&:hover': { backgroundColor: T.cardInner } } },
        cells:      { style: { color: T.text, fontSize: s(13), paddingLeft: s(12), paddingRight: s(12) } },
        pagination: { style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: s(44) },
            pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } } },
        noData:     { style: { backgroundColor: T.card, color: T.muted, padding: s(30) } },
    };

    // ─── 매니저 컬럼 ──────────────────────────────────
    const ManagerColumns = [
        {
            id: 1,
            name: (
                <Checkbox style={{ margin: s(3) }} color={S.accent}
                    value={checkedItems?.length > 0 && checkedItems?.length === (manager as any[])?.length}
                    onChange={(e: any) => {
                        if (e?.target?.checked) {
                            setCheckedItems((manager as any[]).map((el: any) => ({ CAREGIVER_ID: el?.ID, SENSOR_ID: sensorID })));
                        } else setCheckedItems([]);
                    }}
                />
            ),
            cell: (row: any) => (
                <Checkbox style={{ margin: s(3) }} color={S.accent}
                    value={checkedItems.some((el: any) => el?.CAREGIVER_ID === row?.ID)}
                    onChange={(e: any) => {
                        if (e?.target?.checked) setCheckedItems([...checkedItems, { CAREGIVER_ID: row?.ID, SENSOR_ID: sensorID }]);
                        else setCheckedItems(checkedItems.filter((el: any) => el?.CAREGIVER_ID !== row?.ID));
                    }}
                />
            ),
            grow: 0,
        },
        { id: 2, name: '이름',       selector: (row: any) => row.NAME,             grow: 3 },
        { id: 3, name: '전화번호',   selector: (row: any) => row.PHONE,            grow: 3 },
        { id: 4, name: '출퇴근상태', selector: (row: any) => WorkState(row.WORKING), grow: 1 },
    ];

    const SensitivityText = (v: number) => v === 0 ? '강' : v === 1 ? '중' : v === 2 ? '약' : '테스트';
    const StatusText = (code: number) => STATUS_MAP[code]?.label || '';
    const DiaperHistoryColumns = [
        { name: '순서',     selector: (row: any) => row._no,                                          grow: 1 },
        { name: '감지량',   selector: (row: any) => row.maxValue != null ? row.maxValue : '-',         grow: 1 },
        { name: '센서감도', selector: (row: any) => SensitivityText(row.SENSITIVITY),                  grow: 1 },
        { name: '특이사항', selector: (row: any) => (row.STATUS_LIST || []).map(StatusText).join(', ') || '-', grow: 1 },
        { name: '보호자',   selector: (row: any) => (row.caregivers || []).join(', ') || '-',          grow: 1 },
        { name: '시간',     selector: (row: any) => row.end ? moment(row.end).format('HH:mm:ss') : '', grow: 1 },
    ];

    // 중계기 변경 내역 컬럼 (placeholder)
    const RepeaterHistoryColumns = [
        { name: '날짜',       selector: (row: any) => row.REGDATE ? moment(row.REGDATE).format('YYYY-MM-DD HH:mm') : '', grow: 2 },
        { name: '이전 중계기', selector: (row: any) => row.OLD_REPEATER || '', grow: 2 },
        { name: '변경 중계기', selector: (row: any) => row.NEW_REPEATER || '', grow: 2 },
    ];

    // ─── 특이사항 뱃지 목록 ───────────────────────────
    const statusBadges = MemosList.map((item: any) => STATUS_MAP[item.STATUS]).filter(Boolean);

    // ─── 보호사 등록 모달 state ─────────────────────────
    const [regList, setRegList]         = useState<any[]>([]);
    const [regSelected, setRegSelected] = useState<any>();
    const [regResult, setRegResult]     = useState<any[]>([]);
    const [regSearch, setRegSearch]     = useState('');

    useEffect(() => {
        if (modalVisible) {
            setRegSelected(undefined); setRegSearch('');
            Data.fetchData('group/getCaregiverList', { AREA: data?.AREA }, (res: any, cnt: any) => {
                if (cnt > 0) {
                    const filtered = res.filter((el: any) => !(manager as any[]).some((item: any) => item?.ID === el?.ID));
                    setRegResult(filtered); setRegList(filtered);
                } else {
                    setRegResult([]); setRegList([]);
                }
            }, true);
        }
    }, [modalVisible]);

    useEffect(() => {
        if (!regSearch) setRegList(regResult);
        else setRegList(regResult?.filter((item: any) => item?.NAME?.includes(regSearch)));
    }, [regSearch, regResult]);

    const onRegisterCaregiver = () => {
        if (!regSelected) { alert('보호사를 선택해주세요.'); return; }
        Data.fetchData('sensor/insertCaregiverToSensor', {
            CAREGIVER_ID: regSelected?.ID, SENSOR_ID: orignID
        }, (_: any, count: any) => {
            if (count === 1) { alert('등록되었습니다.'); setModalVisible(false); getSensoManagerList(); }
            else alert('등록에 실패하였습니다.');
        }, true);
    };

    const renderRegisterModal = () => {
        if (!modalVisible) return null;
        return createPortal(
            <View style={{ position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
                <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                    <TouchableOpacity activeOpacity={1} onPress={() => setModalVisible(false)} style={{ flex: 1 }} />
                </View>
                <View ref={blockContentDrag} style={[st.modal, { backgroundColor: T.card, borderColor: T.border, zIndex: 10 }]}>
                    <View style={[st.modalHeader, { borderBottomColor: T.border }]}>
                        <Text style={[st.modalTitle, { color: T.text }]}>보호사 등록</Text>
                        <TouchableOpacity onPress={() => setModalVisible(false)}>
                            <AntDesign name="close" size={s(22)} color={T.muted} />
                        </TouchableOpacity>
                    </View>
                    <View style={[st.modalSearch, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                        <Ionicons name="search" size={s(16)} color={T.muted} />
                        <TextInput autoCapitalize="none" placeholder="이름 검색..."
                            placeholderTextColor={T.muted as string}
                            style={[st.modalSearchInput, { color: T.text }]}
                            onChangeText={setRegSearch} value={regSearch} />
                    </View>
                    {regSelected && (
                        <View style={[st.modalSelected, { backgroundColor: S.accent + '10', borderColor: S.accent + '30' }]}>
                            <Dot color={S.accent} size={s(8)} />
                            <Text style={{ fontSize: s(13), fontWeight: '700', color: S.accent, marginLeft: s(8) }}>{regSelected.NAME}</Text>
                            <Text style={{ fontSize: s(12), color: T.textSub, marginLeft: s(12) }}>{regSelected.PHONE}</Text>
                        </View>
                    )}
                    <View style={[st.modalTable, { borderColor: T.border }]}>
                        <DataTable columns={[
                            { name: '이름', selector: (r: any) => r.NAME, grow: 1 },
                            { name: '전화번호', selector: (r: any) => r.PHONE, grow: 1 },
                        ]} data={regList || []} pagination paginationPerPage={5} paginationRowsPerPageOptions={[5]}
                            onRowClicked={(row: any) => setRegSelected(row)} highlightOnHover pointerOnHover
                            customStyles={tableStyles}
                            noDataComponent={<Text style={{ padding: s(20), color: T.muted }}>보호사가 없습니다.</Text>}
                        />
                    </View>
                    <View style={st.modalBtnRow}>
                        <TouchableOpacity style={[st.modalBtn, { backgroundColor: S.accent }]} onPress={onRegisterCaregiver}>
                            <Text style={st.modalBtnText}>등록</Text>
                        </TouchableOpacity>
                        <TouchableOpacity style={[st.modalBtn, { backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }]}
                            onPress={() => setModalVisible(false)}>
                            <Text style={[st.modalBtnText, { color: T.textSub }]}>취소</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>,
            document.body
        );
    };

    // ─── 삭제 모달 ────────────────────────────────────
    const onDeleteConfirm = () => {
        Data.fetchData('sensor/deleteCaregiverBySensor', { DATA: checkedItems }, (_: any, count: any) => {
            if (count > 0) { alert('삭제되었습니다.'); setCheckedItems([]); setDeleteModal(false); getSensoManagerList(); }
        }, true);
    };

    // ─── 렌더 ─────────────────────────────────────────
    const anyModal = sensitivityModal || detectionModal || roomModal || calendarModal || modalVisible || deleteModal || sensorDeleteModal;
    return (
        <View style={{ flex: 1, backgroundColor: T.bg }}>
            {/* ── 헤더 바 ── */}
            <View style={[st.header, { backgroundColor: T.bg }]}>
                <View style={{ flex: 1 }}>
                    <Text style={[st.headerTitle, { color: T.text }]}>{data?.NAME || '센서 상세'}</Text>
                    <Text style={[st.headerSub, { color: T.muted }]}>
                        {data?.ROOM_NO || ''}{data?.BED_NO ? ` · ${data.BED_NO}번 침대` : ''}
                    </Text>
                </View>
                <View style={st.headerBtns}>
                    {admin === '0' && (
                        <TouchableOpacity style={[st.headerActionBtn, { backgroundColor: S.err + '18' }]}
                            onPress={() => { setOvrMsg(''); setOvrCurrent(null); setOverrideModal(true); loadOverrideValues(); }}>
                            <Ionicons name="construct-outline" size={s(14)} color={S.err} />
                            <Text style={[st.headerActionText, { color: S.err }]}>특수 기능</Text>
                        </TouchableOpacity>
                    )}
                    <TouchableOpacity style={[st.headerActionBtn, { backgroundColor: S.warn + '18' }]} onPress={openSensitivityModal}>
                        <Ionicons name="options-outline" size={s(14)} color={S.warn} />
                        <Text style={[st.headerActionText, { color: S.warn }]}>센서감도 설정</Text>
                    </TouchableOpacity>
                    {focus ? (
                        <>
                            <TouchableOpacity style={[st.headerActionBtn, { backgroundColor: S.ok }]} onPress={onSensorUpdate}>
                                <Ionicons name="checkmark" size={s(16)} color="#fff" />
                                <Text style={st.headerActionText}>저장</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[st.headerActionBtn, { backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }]}
                                onPress={onFocus}>
                                <Text style={[st.headerActionText, { color: T.textSub }]}>취소</Text>
                            </TouchableOpacity>
                        </>
                    ) : (
                        <>
                            <TouchableOpacity style={[st.headerActionBtn, { backgroundColor: S.purple + '18' }]} onPress={onFocus}>
                                <Ionicons name="create-outline" size={s(14)} color={S.purple} />
                                <Text style={[st.headerActionText, { color: S.purple }]}>수정</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[st.headerActionBtn, { backgroundColor: S.err + '18' }]} onPress={() => setSensorDeleteModal(true)}>
                                <Ionicons name="trash-outline" size={s(14)} color={S.err} />
                                <Text style={[st.headerActionText, { color: S.err }]}>삭제</Text>
                            </TouchableOpacity>
                        </>
                    )}
                </View>
            </View>

            <ScrollView style={{ flex: 1 }} contentContainerStyle={st.scrollContent} showsVerticalScrollIndicator={false}
                scrollEnabled={!anyModal && !diaperOpen}>

                {/* ── 1. 기본 정보 ── */}
                <View style={[st.card, { backgroundColor: T.card, borderColor: T.border }]}>
                    <SectionTitle title="기본 정보" T={T} />
                    <View style={st.infoGrid}>
                        <View style={st.infoCol}>
                            <InfoRow label="이름"       value={data?.NAME || ''}     T={T} />
                            <InfoRow label="성별"       value={data?.GENDER || ''}   T={T} />
                            <InfoRow label="생년월일"   value={data?.BIRTH ? moment(data.BIRTH).format('YYYY-MM-DD') : ''} T={T} />
                            <InfoRow label="보호자"     value={guardian}             T={T} editable={focus} onChangeText={setGuardian} />
                            <View style={[st.infoRow, { borderBottomColor: T.border }]}>
                                <Text style={[st.infoLabel, { color: T.textSub }]}>담당 보호사</Text>
                                <Text style={[st.infoValue, { color: T.text, flex: 1 }]} numberOfLines={2}>
                                    {(manager && (manager as any[]).length > 0)
                                        ? (manager as any[]).map((m: any) => m?.NAME).filter(Boolean).join(', ')
                                        : '-'}
                                </Text>
                            </View>
                        </View>
                        <View style={st.infoCol}>
                            <InfoRow label="보호자 연락처" value={guardianPhone} T={T} editable={focus} onChangeText={setGuardianPhone} />
                            <View style={[st.infoRow, { borderBottomColor: T.border }]}>
                                <Text style={[st.infoLabel, { color: T.textSub }]}>병실 호수</Text>
                                <Text style={[st.infoValue, { color: T.text, flex: 1 }]}>{roomNo || '-'}</Text>
                                <TouchableOpacity
                                    style={{ flexDirection: 'row', alignItems: 'center', gap: s(3), paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(6), backgroundColor: S.accent + '18' }}
                                    onPress={openRoomModal}>
                                    <Ionicons name="swap-horizontal-outline" size={s(12)} color={S.accent} />
                                    <Text style={{ fontSize: s(11), fontWeight: '700', color: S.accent }}>방 이동</Text>
                                </TouchableOpacity>
                            </View>
                            <InfoRow label="침대 번호"  value={bed}          T={T} editable={focus} onChangeText={setBed} />
                            <InfoRow label="중계기 ID"  value={repeaterID}   T={T} copyable />
                            <InfoRow label="센서 ID"    value={sensorID}     T={T} editable={focus} onChangeText={setSensorID} copyable />
                            <View style={[st.infoRow, { borderBottomColor: T.border }]}>
                                <Text style={[st.infoLabel, { color: T.textSub }]}>신호(RSSI)</Text>
                                <Text style={[st.infoValue, { flex: 1, color: rssiInfo(data?.RSSI).color, fontWeight: '700' }]}>
                                    {(data?.RSSI !== null && data?.RSSI !== undefined && data?.RSSI !== '')
                                        ? `${data.RSSI} dBm (${rssiInfo(data.RSSI).label})`
                                        : '-'}
                                </Text>
                            </View>
                            <View style={[st.infoRow, { borderBottomColor: T.border }]}>
                                <Text style={[st.infoLabel, { color: T.textSub }]}>마지막 수신</Text>
                                <Text style={[st.infoValue, { flex: 1, color: lastSeenInfo(data?.LAST_MIN).color, fontWeight: '700' }]}>
                                    {lastSeenInfo(data?.LAST_MIN).text}
                                </Text>
                            </View>
                        </View>
                    </View>
                </View>

                {/* ── 2. 특이사항 / 움직임 감지 / 기저귀 설정 (3카드 한 줄) ── */}
                {/* 좁은 화면에선 접힌다. flexWrap 없이 flex:1 셋이면 폰에서 카드당 ~110px 이 되어
                    내용이 잘린다. minWidth 를 줘야 줄바꿈이 실제로 일어난다. */}
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: s(16), alignItems: 'stretch', zIndex: 100 }}>
                    {/* 특이사항 */}
                    <View style={[st.card, { flex: 1, minWidth: s(240), backgroundColor: T.card, borderColor: T.border }]}>
                        <SectionTitle title="특이사항" dotColor={S.warn} T={T} />
                        <View style={st.badgeRow}>
                            {[1, 2, 3, 4].map(code => {
                                const m = STATUS_MAP[code];
                                const active = MemosList.some((item: any) => item.STATUS === code);
                                return <StatusBadge key={code} text={m.label} color={m.color} active={active}
                                    onPress={() => toggleMemoStatus(code)} T={T} />;
                            })}
                        </View>
                    </View>

                    {/* 움직임 감지 */}
                    <View style={[st.card, { flex: 1, minWidth: s(240), backgroundColor: T.card, borderColor: T.border }]}>
                        <SectionTitle title="움직임 감지 설정" dotColor={S.accent} T={T} />
                        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: s(8) }}>
                            <View style={[st.timeBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                                <Ionicons name="time-outline" size={s(14)} color={S.accent} />
                                <Text style={{ fontSize: s(13), fontWeight: '600', color: T.text }}>
                                    {fmtTime(detectionSt)} ~ {fmtTime(detectionEnd)}
                                </Text>
                            </View>
                            <TouchableOpacity style={[st.actionBtn, { backgroundColor: S.accent }]} onPress={openDetectionModal}>
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: '#fff' }}>설정</Text>
                            </TouchableOpacity>
                            <ToggleChip label={`움직임 알림 ${detectionAlarm ? '켜짐' : '꺼짐'}`} active={!!detectionAlarm}
                                color={detectionAlarm ? S.accent : T.muted as string} onPress={toggleDetectionAlarm} T={T} />
                            <ToggleChip label={`소변감지 ${urineAlarm ? '켜짐' : '꺼짐'}`} active={!!urineAlarm}
                                color={urineAlarm ? S.warn : T.muted as string} onPress={toggleUrineAlarm} T={T} />
                        </View>
                    </View>

                    {/* 기저귀 설정 */}
                    <View style={[st.card, { flex: 1, minWidth: s(240), backgroundColor: T.card, borderColor: T.border, overflow: 'visible' as any, zIndex: 100 }]}>
                        <View style={{ flex: 1 }}>
                            <SectionTitle title="기저귀 설정" dotColor={S.accent} T={T} />
                            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: s(8) }}>
                                <TouchableOpacity
                                    style={[st.radioBtn, {
                                        backgroundColor: diaperShape === 0 ? S.accent + '18' : T.cardInner,
                                        borderColor: diaperShape === 0 ? S.accent : T.border,
                                    }]}
                                    onPress={() => updateClassification(0)}
                                >
                                    <View style={[st.radioDot, {
                                        borderColor: diaperShape === 0 ? S.accent : T.muted as string,
                                        backgroundColor: diaperShape === 0 ? S.accent : 'transparent',
                                    }]} />
                                    <Text style={{ fontSize: s(12), fontWeight: '600', color: diaperShape === 0 ? S.accent : T.text }}>일자형</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={[st.radioBtn, {
                                        backgroundColor: diaperShape === 1 ? S.accent + '18' : T.cardInner,
                                        borderColor: diaperShape === 1 ? S.accent : T.border,
                                    }]}
                                    onPress={() => updateClassification(1)}
                                >
                                    <View style={[st.radioDot, {
                                        borderColor: diaperShape === 1 ? S.accent : T.muted as string,
                                        backgroundColor: diaperShape === 1 ? S.accent : 'transparent',
                                    }]} />
                                    <Text style={{ fontSize: s(12), fontWeight: '600', color: diaperShape === 1 ? S.accent : T.text }}>삼각체결</Text>
                                </TouchableOpacity>
                                <View style={[st.dividerV, { backgroundColor: T.border }]} />
                                <Text style={{ fontSize: s(13), fontWeight: '600', color: T.textSub }}>종류:</Text>
                                <View style={{ position: 'relative', zIndex: 9000 }}>
                                    <TouchableOpacity
                                        style={[st.typeBox, {
                                            backgroundColor: T.cardInner, borderColor: diaperOpen ? S.accent : T.border,
                                            flexDirection: 'row', alignItems: 'center', gap: s(6),
                                        }]}
                                        onPress={() => setDiaperOpen(!diaperOpen)}
                                    >
                                        <Text style={{ fontSize: s(13), fontWeight: '600', color: diaperType ? T.text : T.muted }}>
                                            {diaperList.find(d => d.DIAPER_CODE === diaperType)?.DIAPER_NAME || diaperType || '선택'}
                                        </Text>
                                        <Ionicons name={diaperOpen ? 'chevron-up' : 'chevron-down'} size={s(14)} color={T.muted} />
                                    </TouchableOpacity>
                                    {diaperOpen && (
                                        <View style={{
                                            position: 'absolute', top: '100%', left: 0, minWidth: s(180),
                                            backgroundColor: T.card, borderWidth: 1, borderColor: T.border,
                                            borderRadius: s(10), marginTop: s(4), overflow: 'hidden',
                                            shadowColor: '#000', shadowOffset: { width: 0, height: 4 },
                                            shadowOpacity: 0.2, shadowRadius: s(16), elevation: 20,
                                            zIndex: 9999,
                                        }}>
                                            <ScrollView style={{ maxHeight: s(150) }} nestedScrollEnabled>
                                                {diaperList.filter(d => d.DIAPER_CODE !== diaperType).map((d: any, i: number, arr: any[]) => (
                                                    <TouchableOpacity key={i}
                                                        onPress={() => updateDiaperCode(d.DIAPER_CODE)}
                                                        style={{
                                                            paddingHorizontal: s(14), paddingVertical: s(10),
                                                            borderBottomWidth: i < arr.length - 1 ? 1 : 0,
                                                            borderBottomColor: T.border,
                                                            backgroundColor: 'transparent',
                                                        }}>
                                                        <Text style={{
                                                            fontSize: s(13), fontWeight: '600',
                                                            color: diaperType === d.DIAPER_CODE ? S.accent : T.text,
                                                        }}>{d.DIAPER_NAME}</Text>
                                                    </TouchableOpacity>
                                                ))}
                                            </ScrollView>
                                        </View>
                                    )}
                                </View>
                            </View>
                        </View>
                    </View>
                </View>

                {/* ── 하단: 4줄 전체폭 (담당보호사 → 기저귀교체내역 → 감지이력 → 알림이력) ── */}

                {/* 1) 담당 보호사 */}
                <View style={{ marginBottom: s(16) }}>
                    <View style={[st.card, { backgroundColor: T.card, borderColor: T.border }]}>
                        <SectionTitle title="담당 보호사" dotColor={S.purple} T={T}
                            right={
                                <View style={{ flexDirection: 'row', gap: s(8) }}>
                                    <TouchableOpacity style={[st.smallBtn, { backgroundColor: S.accent + '18' }]}
                                        onPress={() => { if (readyRef.current) setModalVisible(true); }}>
                                        <Ionicons name="add" size={s(14)} color={S.accent} />
                                        <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>등록</Text>
                                    </TouchableOpacity>
                                    {admin === '0' && checkedItems.length > 0 && (
                                        <TouchableOpacity style={[st.smallBtn, { backgroundColor: S.err + '18' }]}
                                            onPress={() => { if (readyRef.current) setDeleteModal(true); }}>
                                            <Ionicons name="trash-outline" size={s(13)} color={S.err} />
                                            <Text style={{ fontSize: s(12), fontWeight: '700', color: S.err }}>삭제 ({checkedItems.length})</Text>
                                        </TouchableOpacity>
                                    )}
                                </View>
                            }
                        />
                        <View style={[st.tableWrap, { borderColor: T.border }]}>
                            <DataTable columns={ManagerColumns} data={mgrData}
                                customStyles={tableStyles}
                                noDataComponent={<Text style={{ padding: s(20), color: T.muted }}>등록된 담당 보호사가 없습니다.</Text>}
                            />
                            {(manager || []).length > 0 && (
                                <TablePagination rowCount={(manager||[]).length} perPage={mgrPer} page={_mgrCur}
                                    onChangePage={setMgrPage} onChangePerPage={(n)=>{ setMgrPer(n); setMgrPage(1); }}
                                    T={T} s={s} accent={S.accent} options={[5,10,15,20]} />
                            )}
                        </View>
                    </View>
                </View>

                {/* 2) 기저귀 교체 내역 */}
                <View style={{ marginBottom: s(16) }}>
                    <View style={[st.card, { backgroundColor: T.card, borderColor: T.border }]}>
                        <SectionTitle title="기저귀 교체 내역" dotColor={S.err} T={T} />
                        <View style={[st.searchRow, { marginBottom: s(12) }]}>
                            <TouchableOpacity
                                style={[st.dateInput, { backgroundColor: T.cardInner, borderColor: T.border }]}
                                onPress={() => { setCalMonth(moment(diaperDate, 'YYYY-MM-DD')); setCalendarModal(true); }}>
                                <Ionicons name="calendar-outline" size={s(14)} color={T.textSub as string} style={{ marginRight: s(6) }} />
                                <Text style={{ fontSize: s(13), color: T.text }}>{diaperDate}</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[st.actionBtn, { backgroundColor: S.purple }]} onPress={searchDiaperHistory}>
                                <Ionicons name="search" size={s(13)} color="#fff" />
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: '#fff', marginLeft: s(4) }}>검색</Text>
                            </TouchableOpacity>
                            <Text style={{ fontSize: s(14), fontWeight: '600', color: T.text, marginLeft: s(8) }}>
                                총 교체 횟수: <Text style={{ color: S.accent, fontWeight: '800' }}>{diaperCount.toLocaleString()}회</Text>
                            </Text>
                        </View>
                        <View style={[st.tableWrap, { borderColor: T.border }]}>
                            <DataTable
                                columns={DiaperHistoryColumns}
                                data={dpData}
                                customStyles={tableStyles}
                                noDataComponent={<Text style={{ padding: s(20), color: T.muted }}>데이터가 없습니다.</Text>}
                            />
                            {(diaperHistory || []).length > 0 && (
                                <TablePagination rowCount={(diaperHistory||[]).length} perPage={dpPer} page={_dpCur}
                                    onChangePage={setDpPage} onChangePerPage={(n)=>{ setDpPer(n); setDpPage(1); }}
                                    T={T} s={s} accent={S.accent} options={[10,20,30]} />
                            )}
                        </View>
                    </View>
                </View>

                {/* 3) 감지 이력 */}
                <View style={{ marginBottom: s(16) }}>
                    <View style={[st.card, { backgroundColor: T.card, borderColor: T.border }]}>
                        <SectionTitle title="감지 이력" dotColor={S.accent} T={T}
                            right={
                                <View style={{ flexDirection: 'row', gap: s(8), flexWrap: 'wrap' }}>
                                    <TouchableOpacity style={[st.smallBtn, { backgroundColor: S.accent + '18' }]} onPress={loadSensorHistory}>
                                        <Ionicons name="refresh" size={s(13)} color={S.accent} />
                                        <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>새로고침</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity style={[st.smallBtn, { backgroundColor: S.purple + '18' }]} onPress={onDiaperChange}>
                                        <Text style={{ fontSize: s(12), fontWeight: '700', color: S.purple }}>기저귀 교체</Text>
                                    </TouchableOpacity>
                                    {/* 상세정보·분석 = 원시 데이터 화면(개발·연구용) → 최고관리자만 */}
                                    {admin === '0' && (
                                    <TouchableOpacity style={[st.smallBtn, { backgroundColor: S.accent + '18' }]}
                                        onPress={() => navigation.navigate('SensorHistory', { sensorID: orignID })}>
                                        <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>상세정보</Text>
                                    </TouchableOpacity>
                                    )}
                                    {admin === '0' && (
                                    <TouchableOpacity style={[st.smallBtn, { backgroundColor: S.ok + '18' }]}
                                        onPress={() => navigation.navigate('SensorAnalysis', { sensorID: orignID })}>
                                        <Text style={{ fontSize: s(12), fontWeight: '700', color: S.ok }}>분석</Text>
                                    </TouchableOpacity>
                                    )}
                                </View>
                            }
                        />
                        <View style={[st.tableWrap, { borderColor: T.border }]}>
                            <DataTable columns={sensingColumns} data={hisData}
                                customStyles={tableStyles}
                                noDataComponent={<Text style={{ padding: s(20), color: T.muted }}>최근 감지 이력이 없습니다.</Text>}
                            />
                            {(sensorHistory || []).length > 0 && (
                                <TablePagination rowCount={(sensorHistory||[]).length} perPage={hisPer} page={_hisCur}
                                    onChangePage={setHisPage} onChangePerPage={(n)=>{ setHisPer(n); setHisPage(1); }}
                                    T={T} s={s} accent={S.accent} options={[5,10,15,20]} />
                            )}
                        </View>
                    </View>
                </View>

                {/* 4) 알림 이력 */}
                <View>
                    <View style={[st.card, { backgroundColor: T.card, borderColor: T.border }]}>
                        <SectionTitle title="알림 이력" dotColor={S.warn} T={T}
                            right={
                                <TouchableOpacity style={[st.smallBtn, { backgroundColor: S.warn + '18' }]} onPress={loadSensorAlarm}>
                                    <Ionicons name="refresh" size={s(13)} color={S.warn} />
                                    <Text style={{ fontSize: s(12), fontWeight: '700', color: S.warn }}>새로고침</Text>
                                </TouchableOpacity>
                            }
                        />
                        <View style={[st.tableWrap, { borderColor: T.border }]}>
                            <DataTable columns={sensingAlramColumns} data={almData}
                                customStyles={tableStyles}
                                noDataComponent={<Text style={{ padding: s(20), color: T.muted }}>최근 알림 이력이 없습니다.</Text>}
                            />
                            {(SensorAlarmHistory || []).length > 0 && (
                                <TablePagination rowCount={(SensorAlarmHistory||[]).length} perPage={almPer} page={_almCur}
                                    onChangePage={setAlmPage} onChangePerPage={(n)=>{ setAlmPer(n); setAlmPage(1); }}
                                    T={T} s={s} accent={S.accent} options={[5,10,15,20]} />
                            )}
                        </View>
                    </View>
                </View>

            </ScrollView>

            {/* ── 특수기능: 값 오버라이드 모달 (관리자 전용, Portal → document.body) ── */}
            {overrideModal && createPortal(
                <View style={{
                    position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center', zIndex: 9999,
                }}>
                    <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                        <TouchableOpacity activeOpacity={1} onPress={() => setOverrideModal(false)} style={{ flex: 1 }} />
                    </View>
                    <View ref={blockContentDrag} style={[st.confirmModal, { backgroundColor: T.card, borderColor: T.border, width: s(480), maxHeight: '88%' as any, alignItems: 'stretch', zIndex: 10 }]}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(8), marginBottom: s(4) }}>
                            <Ionicons name="construct-outline" size={s(20)} color={S.err} />
                            <Text style={{ fontSize: s(17), fontWeight: '800', color: T.text }}>특수 기능 — 값 오버라이드</Text>
                            <View style={{ flex: 1 }} />
                            <TouchableOpacity onPress={loadOverrideValues}
                                style={{ flexDirection: 'row', alignItems: 'center', gap: s(4), paddingHorizontal: s(10), paddingVertical: s(6), borderRadius: s(8), backgroundColor: S.accent + '18' }}>
                                <Ionicons name="refresh" size={s(13)} color={S.accent} />
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>현재값</Text>
                            </TouchableOpacity>
                        </View>
                        <Text style={{ fontSize: s(12), color: T.muted, marginBottom: s(8) }}>
                            관리자 전용. 센서 open/base 기준값·중계기 값을 서버에 강제 반영합니다. (빈 칸은 변경 안 함)
                        </Text>
                        <Text style={{ fontSize: s(12), color: T.textSub, marginBottom: s(10) }}>
                            대상 센서 ID: <Text style={{ fontWeight: '800', color: S.accent }}>{orignID || sensorID}</Text>
                        </Text>

                        <ScrollView style={{ maxHeight: s(360) }} showsVerticalScrollIndicator={true}>
                            {/* ① 초기값(open) */}
                            <View style={{ marginBottom: s(12), padding: s(12), borderRadius: s(10), backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }}>
                                <Text style={{ fontSize: s(13), fontWeight: '800', color: T.text, marginBottom: s(2) }}>① 초기값 (open) 기준</Text>
                                <Text style={{ fontSize: s(11), color: T.muted, marginBottom: s(8) }}>현재: CAP1 {ovrCurrent?.open?.CAP1 ?? '-'} / CAP2 {ovrCurrent?.open?.CAP2 ?? '-'}</Text>
                                <View style={{ flexDirection: 'row', gap: s(8), marginBottom: s(8) }}>
                                    <TextInput value={ovrOpen.CAP1} onChangeText={t => setOvrOpen(o => ({ ...o, CAP1: t }))} placeholder="CAP1" placeholderTextColor={T.muted as string}
                                        style={{ flex: 1, fontSize: s(14), color: T.text, backgroundColor: T.card, borderWidth: 1, borderColor: T.border, borderRadius: s(8), paddingHorizontal: s(10), paddingVertical: s(8) }} />
                                    <TextInput value={ovrOpen.CAP2} onChangeText={t => setOvrOpen(o => ({ ...o, CAP2: t }))} placeholder="CAP2" placeholderTextColor={T.muted as string}
                                        style={{ flex: 1, fontSize: s(14), color: T.text, backgroundColor: T.card, borderWidth: 1, borderColor: T.border, borderRadius: s(8), paddingHorizontal: s(10), paddingVertical: s(8) }} />
                                </View>
                                <TouchableOpacity onPress={() => sendOverride('overrideSensorOpen', { SENSOR_ID: orignID || sensorID, ...(num(ovrOpen.CAP1) != null ? { CAP1: num(ovrOpen.CAP1) } : {}), ...(num(ovrOpen.CAP2) != null ? { CAP2: num(ovrOpen.CAP2) } : {}) }, '초기값(open)')}
                                    style={{ alignSelf: 'flex-end', paddingHorizontal: s(18), paddingVertical: s(8), borderRadius: s(8), backgroundColor: S.accent }}>
                                    <Text style={{ fontSize: s(13), fontWeight: '800', color: '#fff' }}>적용</Text>
                                </TouchableOpacity>
                            </View>

                            {/* ② 기준값(base) */}
                            <View style={{ marginBottom: s(12), padding: s(12), borderRadius: s(10), backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }}>
                                <Text style={{ fontSize: s(13), fontWeight: '800', color: T.text, marginBottom: s(2) }}>② 기준값 (base)</Text>
                                <Text style={{ fontSize: s(11), color: T.muted, marginBottom: s(8) }}>현재: CAP1 {ovrCurrent?.base?.CAP1 ?? '-'} / CAP2 {ovrCurrent?.base?.CAP2 ?? '-'}</Text>
                                <View style={{ flexDirection: 'row', gap: s(8), marginBottom: s(8) }}>
                                    <TextInput value={ovrBase.CAP1} onChangeText={t => setOvrBase(o => ({ ...o, CAP1: t }))} placeholder="CAP1" placeholderTextColor={T.muted as string}
                                        style={{ flex: 1, fontSize: s(14), color: T.text, backgroundColor: T.card, borderWidth: 1, borderColor: T.border, borderRadius: s(8), paddingHorizontal: s(10), paddingVertical: s(8) }} />
                                    <TextInput value={ovrBase.CAP2} onChangeText={t => setOvrBase(o => ({ ...o, CAP2: t }))} placeholder="CAP2" placeholderTextColor={T.muted as string}
                                        style={{ flex: 1, fontSize: s(14), color: T.text, backgroundColor: T.card, borderWidth: 1, borderColor: T.border, borderRadius: s(8), paddingHorizontal: s(10), paddingVertical: s(8) }} />
                                </View>
                                <TouchableOpacity onPress={() => sendOverride('overrideSensorBase', { SENSOR_ID: orignID || sensorID, ...(num(ovrBase.CAP1) != null ? { CAP1: num(ovrBase.CAP1) } : {}), ...(num(ovrBase.CAP2) != null ? { CAP2: num(ovrBase.CAP2) } : {}) }, '기준값(base)')}
                                    style={{ alignSelf: 'flex-end', paddingHorizontal: s(18), paddingVertical: s(8), borderRadius: s(8), backgroundColor: S.purple }}>
                                    <Text style={{ fontSize: s(13), fontWeight: '800', color: '#fff' }}>적용</Text>
                                </TouchableOpacity>
                            </View>

                        </ScrollView>

                        {ovrMsg ? (
                            <Text style={{ marginTop: s(10), fontSize: s(13), fontWeight: '700', textAlign: 'center',
                                color: ovrMsg.startsWith('✅') ? S.ok : (ovrMsg.startsWith('❌') ? S.err : T.textSub) }}>{ovrMsg}</Text>
                        ) : null}

                        <TouchableOpacity onPress={() => setOverrideModal(false)}
                            style={{ marginTop: s(14), alignSelf: 'center', paddingHorizontal: s(28), paddingVertical: s(10), borderRadius: s(10), backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }}>
                            <Text style={{ fontSize: s(14), fontWeight: '700', color: T.textSub }}>닫기</Text>
                        </TouchableOpacity>
                    </View>
                </View>,
                document.body
            )}

            {/* ── 센서감도 설정 모달 (Portal → document.body) ── */}
            {sensitivityModal && createPortal(
                <View style={{
                    position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    justifyContent: 'center', alignItems: 'center',
                    zIndex: 9999,
                }}>
                    <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                        <TouchableOpacity activeOpacity={1} onPress={() => setSensitivityModal(false)} style={{ flex: 1 }} />
                    </View>
                    <View ref={blockContentDrag} style={[st.confirmModal, { backgroundColor: T.card, borderColor: T.border, width: s(360), zIndex: 10 }]}>
                        <Ionicons name="options-outline" size={s(32)} color={S.warn} style={{ marginBottom: s(12) }} />
                        <Text style={{ fontSize: s(17), fontWeight: '700', color: T.text, marginBottom: s(20) }}>센서감도 설정</Text>
                        <View style={{ width: '100%', gap: s(10), marginBottom: s(20) }}>
                            {([
                                { label: '약', value: 2, desc: '민감도 낮음', color: S.ok },
                                { label: '중', value: 1, desc: '민감도 보통', color: S.warn },
                                { label: '강', value: 0, desc: '민감도 높음', color: S.err },
                            ] as const).map(opt => {
                                const sel = selectedSensitivity === opt.value;
                                return (
                                    <TouchableOpacity key={opt.value}
                                        style={{
                                            flexDirection: 'row', alignItems: 'center', gap: s(12),
                                            paddingHorizontal: s(16), paddingVertical: s(14), borderRadius: s(10),
                                            borderWidth: 1.5,
                                            borderColor: sel ? opt.color : T.border,
                                            backgroundColor: sel ? opt.color + '12' : T.cardInner,
                                        }}
                                        onPress={() => setSelectedSensitivity(opt.value)}
                                    >
                                        <View style={{
                                            width: s(20), height: s(20), borderRadius: s(10), borderWidth: 2,
                                            borderColor: sel ? opt.color : T.muted as string,
                                            justifyContent: 'center', alignItems: 'center',
                                        }}>
                                            {sel && <View style={{ width: s(10), height: s(10), borderRadius: s(5), backgroundColor: opt.color }} />}
                                        </View>
                                        <Text style={{ fontSize: s(16), fontWeight: '700', color: sel ? opt.color : T.text, width: s(24) }}>{opt.label}</Text>
                                        <Text style={{ fontSize: s(13), color: T.textSub }}>{opt.desc}</Text>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>
                        <View style={st.modalBtnRow}>
                            <TouchableOpacity style={[st.modalBtn, { backgroundColor: S.warn }]} onPress={onSensitivityConfirm}>
                                <Text style={st.modalBtnText}>변경</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[st.modalBtn, { backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }]}
                                onPress={() => setSensitivityModal(false)}>
                                <Text style={[st.modalBtnText, { color: T.textSub }]}>취소</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>,
                document.body
            )}

            {/* ── 움직임 감지 시간 설정 모달 (Portal → document.body) ── */}
            {detectionModal && createPortal(
                <View style={{ position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
                    <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                        <TouchableOpacity activeOpacity={1} onPress={() => setDetectionModal(false)} style={{ flex: 1 }} />
                    </View>
                    <View ref={blockContentDrag} style={[st.confirmModal, { backgroundColor: T.card, borderColor: T.border, width: s(480), zIndex: 10 }]}>
                        <Ionicons name="time-outline" size={s(28)} color={S.accent} style={{ marginBottom: s(8) }} />
                        <Text style={{ fontSize: s(17), fontWeight: '700', color: T.text, marginBottom: s(6) }}>움직임 감지 시간 설정</Text>
                        <Text style={{ fontSize: s(12), color: T.textSub, marginBottom: s(20) }}>스크롤하여 시간을 설정하세요</Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(16), marginBottom: s(24) }}>
                            {/* 시작 */}
                            <View style={{ alignItems: 'center' }}>
                                <Text style={{ fontSize: s(12), fontWeight: '600', color: S.accent, marginBottom: s(10) }}>시작 시간</Text>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(4) }}>
                                    <WheelPicker ref={stHRef} value={stH} max={23} onChange={setStH} color={S.accent} T={T}
                                        onEditDone={() => setTimeout(() => stMRef.current?.startEdit(), 100)} />
                                    <Text style={{ fontSize: s(26), fontWeight: '800', color: T.muted }}>:</Text>
                                    <WheelPicker ref={stMRef} value={stM} max={59} onChange={setStM} color={S.accent} T={T}
                                        onEditDone={() => setTimeout(() => endHRef.current?.startEdit(), 100)} />
                                </View>
                            </View>
                            <View style={{ paddingTop: s(24) }}>
                                <Ionicons name="arrow-forward" size={s(22)} color={T.muted} />
                            </View>
                            {/* 종료 */}
                            <View style={{ alignItems: 'center' }}>
                                <Text style={{ fontSize: s(12), fontWeight: '600', color: S.purple, marginBottom: s(10) }}>종료 시간</Text>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(4) }}>
                                    <WheelPicker ref={endHRef} value={endH} max={23} onChange={setEndH} color={S.purple} T={T}
                                        onEditDone={() => setTimeout(() => endMRef.current?.startEdit(), 100)} />
                                    <Text style={{ fontSize: s(26), fontWeight: '800', color: T.muted }}>:</Text>
                                    <WheelPicker ref={endMRef} value={endM} max={59} onChange={setEndM} color={S.purple} T={T} />
                                </View>
                            </View>
                        </View>
                        <View style={st.modalBtnRow}>
                            <TouchableOpacity style={[st.modalBtn, { backgroundColor: S.accent }]} onPress={onDetectionTimeConfirm}>
                                <Text style={st.modalBtnText}>저장</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[st.modalBtn, { backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }]}
                                onPress={() => setDetectionModal(false)}>
                                <Text style={[st.modalBtnText, { color: T.textSub }]}>취소</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>,
                document.body
            )}

            {/* ── 방 이동 모달 (Portal → document.body) ── */}
            {roomModal && createPortal(
                <View style={{
                    position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    justifyContent: 'center', alignItems: 'center',
                    zIndex: 9999,
                }}>
                    <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                        <TouchableOpacity activeOpacity={1} onPress={() => setRoomModal(false)} style={{ flex: 1 }} />
                    </View>
                    <View ref={blockContentDrag} style={[st.confirmModal, { backgroundColor: T.card, borderColor: T.border, width: s(400), zIndex: 10 }]}>
                        <Ionicons name="swap-horizontal-outline" size={s(28)} color={S.accent} style={{ marginBottom: s(8) }} />
                        <Text style={{ fontSize: s(17), fontWeight: '700', color: T.text, marginBottom: s(4) }}>방 이동</Text>
                        <Text style={{ fontSize: s(13), color: T.textSub, marginBottom: s(16) }}>
                            현재: <Text style={{ fontWeight: '700', color: S.accent }}>{roomNo}</Text>
                        </Text>
                        <div ref={(el: any) => {
                            if (!el || el._ws) return; el._ws = true;
                            el.addEventListener('wheel', (e: WheelEvent) => {
                                const atTop = el.scrollTop === 0 && e.deltaY < 0;
                                const atBot = el.scrollTop + el.clientHeight >= el.scrollHeight && e.deltaY > 0;
                                if (atTop || atBot) { e.preventDefault(); }
                                e.stopPropagation();
                            }, { passive: false });
                            el.addEventListener('touchmove', (e: Event) => e.stopPropagation(), { passive: false });
                        }} style={{ width: '100%', maxHeight: s(240), overflowY: 'auto',
                            border: `1px solid ${T.border}`, borderRadius: s(10), marginBottom: s(20) } as any}>
                            {repeaterList.filter(r => r.ROOM_NO !== roomNo).map((r: any, i: number, arr: any[]) => {
                                const sel = selectedRoom?.ROOM_NO === r.ROOM_NO;
                                return (
                                    <TouchableOpacity key={i}
                                        onPress={() => setSelectedRoom(r)}
                                        style={{
                                            flexDirection: 'row', alignItems: 'center', gap: s(10),
                                            paddingHorizontal: s(14), paddingVertical: s(12),
                                            borderBottomWidth: i < arr.length - 1 ? 1 : 0,
                                            borderBottomColor: T.border,
                                            backgroundColor: sel ? S.accent + '12' : 'transparent',
                                        }}>
                                        <View style={{
                                            width: s(18), height: s(18), borderRadius: s(9), borderWidth: 2,
                                            borderColor: sel ? S.accent : T.muted as string,
                                            justifyContent: 'center', alignItems: 'center',
                                        }}>
                                            {sel && <View style={{ width: s(10), height: s(10), borderRadius: s(5), backgroundColor: S.accent }} />}
                                        </View>
                                        <Text style={{
                                            fontSize: s(14), fontWeight: '600',
                                            color: sel ? S.accent : T.text,
                                        }}>{r.ROOM_NO}</Text>
                                    </TouchableOpacity>
                                );
                            })}
                        </div>
                        {selectedRoom && (
                            <Text style={{ fontSize: s(13), color: S.warn, marginBottom: s(12), textAlign: 'center' }}>
                                {roomNo} → {selectedRoom.ROOM_NO}(으)로 이동합니다
                            </Text>
                        )}
                        <View style={st.modalBtnRow}>
                            <TouchableOpacity style={[st.modalBtn, { backgroundColor: S.accent, opacity: selectedRoom ? 1 : 0.4 }]}
                                onPress={onRoomTransfer} disabled={!selectedRoom}>
                                <Text style={st.modalBtnText}>이동</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[st.modalBtn, { backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }]}
                                onPress={() => setRoomModal(false)}>
                                <Text style={[st.modalBtnText, { color: T.textSub }]}>취소</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>,
                document.body
            )}

            {/* ── 달력 모달 ── */}
            {calendarModal && createPortal(
                <View style={{ position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
                    <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                        <TouchableOpacity activeOpacity={1} onPress={() => setCalendarModal(false)} style={{ flex: 1 }} />
                    </View>
                    <View ref={blockContentDrag} style={[st.confirmModal, { width: s(340), padding: 0,
                        backgroundColor: T.card, borderColor: T.border, zIndex: 10, overflow: 'hidden' }]}>
                        {/* 헤더 */}
                        <View style={{ backgroundColor: dark ? 'rgba(56,189,248,0.08)' : 'rgba(56,189,248,0.06)',
                            paddingVertical: s(16), paddingHorizontal: s(20), borderBottomWidth: 1, borderBottomColor: T.border }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                <TouchableOpacity onPress={() => setCalMonth(m => moment(m).subtract(1, 'month'))}
                                    style={{ width: s(32), height: s(32), borderRadius: s(8), backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                        justifyContent: 'center', alignItems: 'center' }}>
                                    <AntDesign name="left" size={s(14)} color={T.textSub as string} />
                                </TouchableOpacity>
                                <View style={{ alignItems: 'center' }}>
                                    <Text style={{ fontSize: s(16), fontWeight: '700', color: T.text, letterSpacing: 0.5 }}>
                                        {calMonth.format('YYYY')}
                                    </Text>
                                    <Text style={{ fontSize: s(13), fontWeight: '600', color: S.accent, marginTop: s(2) }}>
                                        {calMonth.format('M월')}
                                    </Text>
                                </View>
                                <TouchableOpacity onPress={() => setCalMonth(m => moment(m).add(1, 'month'))}
                                    style={{ width: s(32), height: s(32), borderRadius: s(8), backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                        justifyContent: 'center', alignItems: 'center' }}>
                                    <AntDesign name="right" size={s(14)} color={T.textSub as string} />
                                </TouchableOpacity>
                            </View>
                        </View>
                        {/* 요일 헤더 */}
                        <View style={{ flexDirection: 'row', paddingHorizontal: s(12), paddingTop: s(14), paddingBottom: s(8) }}>
                            {['일','월','화','수','목','금','토'].map((d, i) => (
                                <View key={d} style={{ flex: 1, alignItems: 'center' }}>
                                    <Text style={{ fontSize: s(11), fontWeight: '700', letterSpacing: 1,
                                        color: i === 0 ? 'rgba(248,113,113,0.7)' : i === 6 ? 'rgba(56,189,248,0.7)' : (T.muted as string) }}>{d}</Text>
                                </View>
                            ))}
                        </View>
                        {/* 구분선 */}
                        <View style={{ height: 1, backgroundColor: T.border, marginHorizontal: s(16) }} />
                        {/* 날짜 그리드 */}
                        <View style={{ paddingHorizontal: s(10), paddingTop: s(8), paddingBottom: s(12) }}>
                        {(() => {
                            const startOfMonth = moment(calMonth).startOf('month');
                            const endOfMonth = moment(calMonth).endOf('month');
                            const startDay = startOfMonth.day();
                            const daysInMonth = endOfMonth.date();
                            const today = moment().format('YYYY-MM-DD');
                            const rows: React.ReactNode[] = [];
                            let cells: React.ReactNode[] = [];
                            for (let i = 0; i < startDay; i++) {
                                cells.push(<View key={`e${i}`} style={{ flex: 1, height: s(38) }} />);
                            }
                            for (let day = 1; day <= daysInMonth; day++) {
                                const dateStr = moment(calMonth).date(day).format('YYYY-MM-DD');
                                const isSelected = dateStr === diaperDate;
                                const isToday = dateStr === today;
                                const dayOfWeek = (startDay + day - 1) % 7;
                                cells.push(
                                    <TouchableOpacity key={day} style={{ flex: 1, height: s(38), alignItems: 'center', justifyContent: 'center' }}
                                        onPress={() => {
                                            const sel = moment(calMonth).date(day).format('YYYY-MM-DD');
                                            setDiaperDate(sel);
                                            setCalendarModal(false);
                                            setTimeout(() => {
                                                Data.fetchData('sensor/dateTimeFilteredHistory2', {
                                                    SENSOR_ID: data?.ORIGINAL_ID || sensorID,
                                                    STARTDATE: sel, ENDDATE: sel,
                                                }, (result: any, count: any) => {
                                                    if (count > 0 && Array.isArray(result)) {
                                                        const events = result.filter((r: any) => r.start && r.end);
                                                        setDiaperHistory(events.map((e: any, i: number) => ({ ...e, _no: i + 1 })));
                                                        setDiaperCount(events.length);
                                                    } else { setDiaperHistory([]); setDiaperCount(0); }
                                                }, true);
                                            }, 100);
                                        }}>
                                        <View style={{ width: s(32), height: s(32), borderRadius: s(10), alignItems: 'center', justifyContent: 'center',
                                            backgroundColor: isSelected ? S.accent : isToday ? (dark ? 'rgba(56,189,248,0.12)' : 'rgba(56,189,248,0.08)') : 'transparent',
                                            borderWidth: isToday && !isSelected ? 1 : 0,
                                            borderColor: isToday ? 'rgba(56,189,248,0.3)' : 'transparent' }}>
                                            <Text style={{ fontSize: s(13),
                                                fontWeight: isSelected ? '800' : isToday ? '700' : '500',
                                                color: isSelected ? '#fff' : isToday ? S.accent
                                                    : dayOfWeek === 0 ? S.err : dayOfWeek === 6 ? S.accent
                                                    : (T.text as string) }}>
                                                {day}
                                            </Text>
                                        </View>
                                    </TouchableOpacity>
                                );
                                if ((startDay + day) % 7 === 0 || day === daysInMonth) {
                                    while (cells.length < 7) cells.push(<View key={`f${cells.length}`} style={{ flex: 1, height: s(38) }} />);
                                    rows.push(<View key={`r${rows.length}`} style={{ flexDirection: 'row', marginBottom: s(2) }}>{cells}</View>);
                                    cells = [];
                                }
                            }
                            return rows;
                        })()}
                        </View>
                        {/* 하단 버튼 */}
                        <View style={{ flexDirection: 'row', gap: s(10), paddingHorizontal: s(16), paddingBottom: s(16), paddingTop: s(4), justifyContent: 'center' }}>
                            <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', gap: s(5),
                                paddingHorizontal: s(16), paddingVertical: s(8), borderRadius: s(8),
                                backgroundColor: dark ? 'rgba(56,189,248,0.12)' : 'rgba(56,189,248,0.08)',
                                borderWidth: 1, borderColor: 'rgba(56,189,248,0.2)' }}
                                onPress={() => {
                                    const today = moment().format('YYYY-MM-DD');
                                    setDiaperDate(today);
                                    setCalMonth(moment());
                                    setCalendarModal(false);
                                    setTimeout(() => {
                                        Data.fetchData('sensor/dateTimeFilteredHistory2', {
                                            SENSOR_ID: data?.ORIGINAL_ID || sensorID,
                                            STARTDATE: today, ENDDATE: today,
                                        }, (result: any, count: any) => {
                                            if (count > 0 && Array.isArray(result)) {
                                                const events = result.filter((r: any) => r.start && r.end);
                                                setDiaperHistory(events.map((e: any, i: number) => ({ ...e, _no: i + 1 })));
                                                setDiaperCount(events.length);
                                            } else { setDiaperHistory([]); setDiaperCount(0); }
                                        }, true);
                                    }, 100);
                                }}>
                                <Ionicons name="today-outline" size={s(13)} color={S.accent} />
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>오늘</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={{ paddingHorizontal: s(16), paddingVertical: s(8), borderRadius: s(8),
                                backgroundColor: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                borderWidth: 1, borderColor: T.border }}
                                onPress={() => setCalendarModal(false)}>
                                <Text style={{ fontSize: s(12), fontWeight: '600', color: T.textSub }}>닫기</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>,
                document.body
            )}

            {renderRegisterModal()}
            {deleteModal && createPortal(
                <View style={{ position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
                    <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                        <TouchableOpacity activeOpacity={1} onPress={() => setDeleteModal(false)} style={{ flex: 1 }} />
                    </View>
                    <View ref={blockContentDrag} style={[st.confirmModal, { backgroundColor: T.card, borderColor: T.border, zIndex: 10 }]}>
                        <Ionicons name="warning-outline" size={s(36)} color={S.err} style={{ marginBottom: s(12) }} />
                        <Text style={{ fontSize: s(16), fontWeight: '600', color: T.text, textAlign: 'center', lineHeight: s(24) }}>
                            선택한 담당 보호사 {checkedItems.length}명을{'\n'}삭제하시겠습니까?
                        </Text>
                        <Text style={{ fontSize: s(12), color: S.err, marginTop: s(8) }}>이 작업은 되돌릴 수 없습니다.</Text>
                        <View style={[st.modalBtnRow, { marginTop: s(20) }]}>
                            <TouchableOpacity style={[st.modalBtn, { backgroundColor: S.err }]} onPress={() => {
                                if (window.confirm('정말 삭제하시겠습니까?')) onDeleteConfirm();
                            }}>
                                <Text style={st.modalBtnText}>삭제</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[st.modalBtn, { backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }]}
                                onPress={() => setDeleteModal(false)}>
                                <Text style={[st.modalBtnText, { color: T.textSub }]}>취소</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>,
                document.body
            )}

            {/* ── 센서 삭제 확인 모달 ── */}
            {sensorDeleteModal && createPortal(
                <View style={{ position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
                    <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                        <TouchableOpacity activeOpacity={1} onPress={() => setSensorDeleteModal(false)} style={{ flex: 1 }} />
                    </View>
                    <View ref={blockContentDrag} style={[st.confirmModal, { backgroundColor: T.card, borderColor: T.border, zIndex: 10 }]}>
                        <Ionicons name="warning-outline" size={s(36)} color={S.err} style={{ marginBottom: s(12) }} />
                        <Text style={{ fontSize: s(16), fontWeight: '700', color: T.text, textAlign: 'center', lineHeight: s(24) }}>
                            해당 센서를 삭제하시겠습니까?
                        </Text>
                        <Text style={{ fontSize: s(12), color: S.err, marginTop: s(8) }}>이 작업은 되돌릴 수 없습니다.</Text>
                        <View style={[st.modalBtnRow, { marginTop: s(20) }]}>
                            <TouchableOpacity style={[st.modalBtn, { backgroundColor: S.err }]} onPress={() => {
                                setSensorDeleteModal(false);
                                onSensorDelete();
                            }}>
                                <Text style={st.modalBtnText}>삭제</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[st.modalBtn, { backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }]}
                                onPress={() => setSensorDeleteModal(false)}>
                                <Text style={[st.modalBtnText, { color: T.textSub }]}>취소</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>,
                document.body
            )}
        </View>
    );
}

// ────────────────────────────────────────────────────────
// 스타일
// ────────────────────────────────────────────────────────
const makeSt = (s: ScaleFn) => StyleSheet.create({
    header: {
        flexDirection: 'row', alignItems: 'center',
        paddingHorizontal: s(16), paddingVertical: s(12),
        gap: s(12),
    },
    backBtn: { width: s(36), height: s(36), borderRadius: s(10), justifyContent: 'center', alignItems: 'center' },
    headerTitle: { fontSize: s(17), fontWeight: '700' },
    headerSub:   { fontSize: s(12), marginTop: s(2) },
    headerBtns:  { flexDirection: 'row', gap: s(8) },
    headerActionBtn: {
        flexDirection: 'row', alignItems: 'center', gap: s(5),
        paddingHorizontal: s(12), paddingVertical: s(7), borderRadius: s(8),
    },
    headerActionText: { fontSize: s(13), fontWeight: '700', color: '#fff' },

    scrollContent: { paddingHorizontal: s(16), paddingBottom: s(40), gap: s(16) },

    card: {
        borderRadius: s(14), borderWidth: 1, padding: s(18),
        shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06, shadowRadius: s(8), elevation: 3,
    },
    sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: s(8), marginBottom: s(14) },
    sectionTitle:  { fontSize: s(15), fontWeight: '700', flex: 1 },

    // 좁은 화면에선 1단으로 접는다. 2단을 유지하면 폰에서 값 영역이 70px 밖에 안 남아
    // 중계기 ID 같은 긴 값이 잘린다.
    infoGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: s(24) },
    infoCol:  { flex: 1, minWidth: s(260) },
    infoRow:  { flexDirection: 'row', alignItems: 'center', paddingVertical: s(10), borderBottomWidth: 1 },
    infoLabel: { width: s(100), fontSize: s(13), fontWeight: '600' },
    infoValue: { flex: 1, fontSize: s(14), fontWeight: '500' },
    infoInput: { flex: 1, fontSize: s(14), paddingVertical: s(6), paddingHorizontal: s(10), borderWidth: 1, borderRadius: s(8) },

    badgeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: s(8), justifyContent: 'center' },
    badge:    { paddingHorizontal: s(18), paddingVertical: s(10), borderRadius: s(10) },

    settingRow: { flexDirection: 'row', alignItems: 'center', gap: s(10), flexWrap: 'wrap' },
    timeBox: {
        flexDirection: 'row', alignItems: 'center', gap: s(8),
        paddingHorizontal: s(14), paddingVertical: s(10), borderRadius: s(10), borderWidth: 1,
    },
    toggleChip: {
        flexDirection: 'row', alignItems: 'center', gap: s(6),
        paddingHorizontal: s(12), paddingVertical: s(8), borderRadius: s(8), borderWidth: 1,
    },
    actionBtn: {
        flexDirection: 'row', alignItems: 'center',
        paddingHorizontal: s(14), paddingVertical: s(8), borderRadius: s(8),
    },

    radioBtn: {
        flexDirection: 'row', alignItems: 'center', gap: s(6),
        paddingHorizontal: s(12), paddingVertical: s(8), borderRadius: s(8), borderWidth: 1,
    },
    radioDot: { width: s(12), height: s(12), borderRadius: s(6), borderWidth: 2 },
    sectionDivider: { borderTopWidth: 1, marginTop: s(16), marginBottom: s(2), paddingTop: 0 },
    dividerV: { width: 1, height: s(28), marginHorizontal: s(4) },
    typeBox:  { paddingHorizontal: s(14), paddingVertical: s(8), borderRadius: s(8), borderWidth: 1 },

    searchRow: { flexDirection: 'row', alignItems: 'center', gap: s(8) },
    dateInput: {
        flexDirection: 'row', alignItems: 'center',
        paddingHorizontal: s(12), paddingVertical: s(8), borderRadius: s(8), borderWidth: 1, width: s(150),
    },

    smallBtn: {
        flexDirection: 'row', alignItems: 'center', gap: s(4),
        paddingHorizontal: s(10), paddingVertical: s(5), borderRadius: s(8),
    },
    tableWrap: { borderWidth: 1, borderRadius: s(10), overflow: 'hidden' },

    // 모달
    modal: {
        alignSelf: 'center', width: '70%', maxWidth: s(700),
        borderRadius: s(16), borderWidth: 1, padding: s(24),
        shadowColor: '#000', shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.15, shadowRadius: s(20), elevation: 10,
    },
    modalHeader: {
        flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
        paddingBottom: s(14), marginBottom: s(14), borderBottomWidth: 1,
    },
    modalTitle: { fontSize: s(17), fontWeight: '700' },
    modalSearch: {
        flexDirection: 'row', alignItems: 'center', gap: s(8),
        borderRadius: s(10), borderWidth: 1, paddingHorizontal: s(12), paddingVertical: s(8), marginBottom: s(12),
    },
    modalSearchInput: { flex: 1, fontSize: s(14) },
    modalSelected: {
        flexDirection: 'row', alignItems: 'center',
        paddingHorizontal: s(12), paddingVertical: s(8),
        borderRadius: s(8), borderWidth: 1, marginBottom: s(12),
    },
    modalTable: { borderWidth: 1, borderRadius: s(10), overflow: 'hidden', marginBottom: s(16) },
    modalBtnRow: { flexDirection: 'row', justifyContent: 'center', gap: s(12) },
    modalBtn: { paddingHorizontal: s(24), paddingVertical: s(10), borderRadius: s(10), alignItems: 'center', minWidth: s(90) },
    modalBtnText: { fontSize: s(14), fontWeight: '700', color: '#fff' },
    confirmModal: {
        alignSelf: 'center', width: s(340), borderRadius: s(16), borderWidth: 1, padding: s(30),
        alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.15, shadowRadius: s(20), elevation: 10,
    },
});
